import { faClipboardList } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import AssignmentIcon from '@mui/icons-material/Assignment';
import {
  Box,
  Button,
  CircularProgress,
  Tooltip as MuiTooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import { useAtomValue, useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';

import { selectedVisitAtom } from '../../../../app/appHeader/imageViewerMenu/atoms';
import { useGetDateFNSLocale } from '../../../../locales/helpers';
import { usePreferences } from '../../../../react-query/usePreferences';
import {
  black,
  blueBlack700,
  orange700,
} from '../../../../theme/videa-style-tokens';
import { useIsInsurancePDFEnabled } from '../../../../utils/hooks/useIsInsurancePDFEnabled';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { colors, layout } from '../../../../utils/theme';
import { ClaimSummary } from '../../../claims/summary/ClaimSummary';
import {
  useSummaryImages,
  useSummaryVisit,
} from '../../../claims/summary/hooks/useSummary';
import { claimSummaryDialogAtom } from '../../../claims/summary/state';
import { useClaimVisit } from '../../../insuranceExport/hooks/useClaimVisit';
import { selectUnifiedIdAtom } from '../../../patientList/atoms';

const localStyles = makeStyles((theme) => ({
  footer: {
    position: 'fixed',
    bottom: 0,
    right: 0,
    overflow: 'hidden',
    width: layout.FINDINGS_SIDEBAR_WIDTH,
    backgroundColor: blueBlack700,
    borderTop: `1px solid ${black}`,
  },
  footerContent: {
    padding: theme.spacing(1),
  },
  buttonContainer: {
    display: 'flex',
    padding: '.125em',
  },
  button: {
    textTransform: 'none',
    textAlign: 'center',
    margin: 'auto',
    width: '100%',
    backgroundColor: colors.BLUE,
    color: colors.WHITE,
    '&:hover': {
      backgroundColor: colors.white,
      color: colors.BLUE,
    },
    '& .beta-tag': {
      display: 'inline-block',
      borderRadius: '1em',
      backgroundColor: orange700,
      color: colors.WHITE,
      padding: '0.125em 0.5em',
      marginLeft: '0.5em',
      fontSize: '0.375em',
      fontWeight: 'bold',
    },
  },
  spinner: {
    textAlign: 'center',
    margin: 'auto',
  },
}));

export const InsuranceClaimFooter = ({
  patientInfo,
  estimatedVisitId,
  isLoading,
  onToggleIsInsuranceClaimPdfOpen,
}: {
  patientInfo: { name: string; formattedDob: string; dob?: string };
  estimatedVisitId: string;
  isLoading: boolean;
  onToggleIsInsuranceClaimPdfOpen?: () => void;
}) => {
  // state
  const setDialogValues = useSetAtom(claimSummaryDialogAtom);
  const unifiedId = useAtomValue(selectUnifiedIdAtom);
  const setSelectedVisit = useSetAtom(selectedVisitAtom);

  // hooks
  const { id: imagingPatientId } = useParams<{ id: string }>();
  const { data: prefs } = usePreferences();
  const classes = localStyles();
  const isInsurancePDFEnabled = useIsInsurancePDFEnabled();
  const { trackEvent } = useTrackEvent();

  const { visits } = useClaimVisit({
    imagingPatientId,
    appointmentOn: '',
  });

  const selectedVisit = visits?.find((v) => v.id === Number(estimatedVisitId));

  const { treatments } = useSummaryImages({
    unifiedAppointmentId: parseInt(unifiedId),
    defaultEstimatedVisitId: estimatedVisitId,
    defaultImagingPatientId: imagingPatientId,
    appointmentImagingPatientId: imagingPatientId,
    appointmentStart: selectedVisit?.visitDate?.toString() ?? '',
    preferences: prefs,
  });

  const { radiographsTakenDate } = useSummaryVisit({
    selectedVisitId: estimatedVisitId,
    selectedPatientId: null,
    appointmentImagingPatientId: imagingPatientId ?? null,
    appointmentStart: selectedVisit?.visitDate?.toString() ?? '',
    insView: 'appointment',
  });

  // variables
  const showInsModalButton = isInsurancePDFEnabled && prefs?.insSummaryModal;
  const visitDate = selectedVisit?.visitDate;
  const locale = useGetDateFNSLocale();

  const handleClick = () => {
    if (showInsModalButton) {
      const visitId = estimatedVisitId || null;
      setSelectedVisit(visitId);
      setDialogValues({
        isOpen: true,
        insView:
          prefs.insuranceExportPreviewPages === 'single'
            ? 'preview-single'
            : 'preview-pages',
        unifiedId,
        estimatedVisitId: visitId ?? '',
        view: 'image',
        patientInfo: {
          dob: patientInfo.formattedDob,
          name: patientInfo.name,
          dateOfVisit: visitDate?.toString() || '',
          radiographsTakenDate,
          providerName: '',
        },
        imagingPatientId,
        locale,
      });

      trackEvent(mixpanelEvents.viewFreeAttachment, {
        unifiedId,
        estimatedVisitId,
        entryPoint: 'Clinical Viewer',
        txCode: treatments?.map((t) => t.txCode),
        attachmentType:
          prefs.insuranceExportPreviewPages === 'single' ? 'Digital' : 'Print',
      });

      trackEvent(mixpanelEvents.insuranceSummary, {
        source: 'summary-only button viewer',
      });

      return;
    }
    onToggleIsInsuranceClaimPdfOpen?.();
  };

  if (!isInsurancePDFEnabled) {
    return null;
  }

  if (prefs.useSidebarRedesign) {
    if (isLoading) {
      return <Skeleton className="vai-h-9" />;
    }

    return (
      <>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                disabled={isLoading}
                className="vai-bg-zinc-800 hover:vai-bg-zinc-900 vai-h-9 vai-flex vai-items-center vai-gap-2"
                onClick={handleClick}
              >
                <FontAwesomeIcon
                  className="vai-text-zinc-50"
                  size="lg"
                  icon={faClipboardList}
                />
                <p className="vai-font-inter vai-capitalize vai-text-sm vai-font-medium vai-text-zinc-50">
                  <Trans>Create Claim Summary</Trans>
                </p>
                <div className="vai-bg-orange-500 vai-rounded-full vai-py-[2px] vai-px-1">
                  <p className="vai-font-inter vai-text-[7px] vai-font-bold vai-text-zinc-50 vai-leading-[8px]">
                    <Trans>BETA</Trans>
                  </p>
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="vai-max-w-sm vai-bg-zinc-950 vai-border-zinc-800 vai-text-slate-100">
              <p className="vai-font-inter vai-text-sm">
                <Trans>
                  Submitting to insurance? In less than 30 seconds, generate a
                  claim summary directly from Videa with original and annotated
                  images to back up your claim.
                </Trans>
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {!!prefs?.insSummaryModal && <ClaimSummary />}
      </>
    );
  }

  return (
    <>
      <Box className={classes.footer}>
        <Box className={classes.footerContent}>
          <div className={classes.buttonContainer}>
            {isLoading ? (
              <CircularProgress className={classes.spinner} />
            ) : (
              <MuiTooltip
                title="Submitting to insurance? In less than 30 seconds, generate a claim summary directly from Videa
            with original and annotated images to back up your claim."
              >
                <Button
                  color="inherit"
                  variant="contained"
                  startIcon={<AssignmentIcon />}
                  className={classes.button}
                  onClick={handleClick}
                >
                  <Trans>Create Claim Summary</Trans>
                  <div className="beta-tag">
                    <Trans>BETA</Trans>
                  </div>
                </Button>
              </MuiTooltip>
            )}
          </div>
        </Box>
      </Box>
      {!!prefs?.insSummaryModal && <ClaimSummary />}
    </>
  );
};
